// Component: Sheet.jsx
// Purpose: THE DS bottom-sheet primitive (shared web + mobile). Two detents:
//   • "auto" — content-height bottom sheet with a grabber + finger-following
//     drag-to-dismiss (backdrop dim behind). The card tracks the thumb 1:1
//     while dragging; on release it either flies off carrying the flick's
//     velocity (past ~110px OR ~550px/s) or springs back. Dragging only starts
//     on a downward pull from the grabber or from content already at the top,
//     so scrolling the body is never hijacked.
//   • "full" — a 100dvh opaque cover that slides up (the
//     MobileInstrumentSearch pattern). No grabber/drag — a scrollable list
//     inside would fight drag-y; dismiss via onClose only. Honors --safe-top.
//
//   Motion: SPRING_SMOOTH slide + a fade on the backdrop. Styling lives in
//   ds-components.css (.ds-sheet-*).
//
//   Body-scroll lock: while open, background scroll is locked — same
//   save/restore-of-document.body.style.overflow approach as Modal.jsx (no
//   scrollbar-width compensation there either, so none here). Unlike Modal,
//   Sheets can stack (e.g. a confirm sheet opened from within another sheet),
//   so the lock is reference-counted at module scope: the FIRST open sets
//   overflow:hidden and remembers the prior value, the LAST close restores
//   it. Opt out per-instance with `lockScroll={false}`.
//
// Usage:
//   <Sheet open={open} onClose={close} title="More">…rows…</Sheet>
//   <Sheet open={open} onClose={close} detent="full">…search UI…</Sheet>
//   <Sheet open={open} onClose={close} lockScroll={false}>…won't lock body scroll…</Sheet>

import React, { useEffect, useRef } from "react";
import { AnimatePresence, motion, animate, useMotionValue } from "framer-motion";
import { cx } from "./cx";
import { SPRING_SMOOTH } from "./motion";
import { haptics } from "./haptics";

// Drag-dismiss thresholds: close if pulled past this far OR flicked this fast.
const DISMISS_OFFSET = 110; // px of downward pull
const DISMISS_VELOCITY = 550; // px/s downward at release
const DECIDE = 4; // px of travel before we commit the gesture to drag vs scroll

// Module-level open-count for the body-scroll lock, so N stacked Sheets only
// toggle document.body.style.overflow on the 0→1 (lock) and 1→0 (restore)
// transitions — mirrors Modal.jsx's save/restore, generalized for stacking.
let sheetLockCount = 0;
let sheetLockPrevOverflow = null;

function acquireBodyScrollLock() {
  if (sheetLockCount === 0) {
    sheetLockPrevOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";
  }
  sheetLockCount += 1;
}

function releaseBodyScrollLock() {
  sheetLockCount = Math.max(0, sheetLockCount - 1);
  if (sheetLockCount === 0) {
    document.body.style.overflow = sheetLockPrevOverflow;
    sheetLockPrevOverflow = null;
  }
}

function SheetHeader({ title, dismissible, onClose }) {
  if (title == null) return null;
  return (
    <div className="ds-sheet-header">
      <span className="ds-sheet-title">{title}</span>
      {dismissible && (
        <button type="button" className="ds-sheet-done" onClick={() => onClose?.()}>
          Done
        </button>
      )}
    </div>
  );
}

export function Sheet({
  open,
  onClose,
  title,
  children,
  dismissible = true,
  detent = "auto",
  lockScroll = true,
  className,
  ...props
}) {
  const isFull = detent === "full";

  // -- Finger-following drag-to-dismiss (auto detent) -----------------------
  // `dragY` is the sheet's live downward offset (0 = open). We drive it 1:1 with
  // the finger while dragging, so the card tracks the thumb — pull slow it moves
  // slow, hold and it holds. On release we hand the offset to a spring seeded
  // with the release velocity: a fast flick flies off, a gentle pull past the
  // threshold eases off, anything short springs back. Scrolling is preserved by
  // only starting a drag on a DOWNWARD pull that begins on the grabber or with
  // the content already at its top — otherwise the touch is left to scroll.
  const dragY = useMotionValue(0);
  const sheetRef = useRef(null);
  const grabRef = useRef(null);
  const bodyRef = useRef(null);
  const g = useRef({ dragging: false, decided: false, startY: 0, lastY: 0, lastT: 0, vy: 0, fromGrab: false });

  useEffect(() => {
    if (open) dragY.set(0); // fresh offset each time it opens
  }, [open, dragY]);

  useEffect(() => {
    const el = sheetRef.current;
    if (!open || isFull || !dismissible || !el) return undefined;

    const onStart = (e) => {
      const t = e.touches[0];
      g.current = {
        dragging: false,
        decided: false,
        startY: t.clientY,
        lastY: t.clientY,
        lastT: performance.now(),
        vy: 0,
        fromGrab: !!grabRef.current && grabRef.current.contains(e.target),
      };
    };

    const onMove = (e) => {
      const s = g.current;
      const y = e.touches[0].clientY;
      const dy = y - s.startY;
      if (!s.decided) {
        if (Math.abs(dy) < DECIDE) return;
        const atTop = !bodyRef.current || bodyRef.current.scrollTop <= 0;
        // Commit to a drag only for a downward pull from the grabber or from
        // content already at the top; everything else stays a native scroll.
        s.decided = true;
        s.dragging = dy > 0 && (s.fromGrab || atTop);
        if (!s.dragging) return;
      }
      if (!s.dragging) return;
      e.preventDefault(); // we own this gesture now — no native scroll/bounce
      const now = performance.now();
      const dt = now - s.lastT;
      if (dt > 0) s.vy = ((y - s.lastY) / dt) * 1000; // px/s
      s.lastY = y;
      s.lastT = now;
      dragY.set(Math.max(0, dy));
    };

    const onEnd = () => {
      const s = g.current;
      const wasDragging = s.dragging;
      s.dragging = false;
      s.decided = false;
      if (!wasDragging) return;
      const offset = dragY.get();
      const vy = s.vy;
      if (offset > DISMISS_OFFSET || vy > DISMISS_VELOCITY) {
        haptics.tap();
        const h = el.offsetHeight || 600;
        // Fly the card off, carrying the flick's velocity, then close.
        animate(dragY, h + 24, {
          type: "spring",
          stiffness: SPRING_SMOOTH.stiffness,
          damping: SPRING_SMOOTH.damping,
          velocity: vy,
          restDelta: 0.5,
        }).then(() => onClose?.());
      } else {
        // Didn't cross the line → spring back to open, honoring the momentum.
        animate(dragY, 0, { type: "spring", stiffness: 420, damping: 38, velocity: vy });
      }
    };

    el.addEventListener("touchstart", onStart, { passive: true });
    el.addEventListener("touchmove", onMove, { passive: false }); // non-passive → preventDefault works
    el.addEventListener("touchend", onEnd, { passive: true });
    el.addEventListener("touchcancel", onEnd, { passive: true });
    return () => {
      el.removeEventListener("touchstart", onStart);
      el.removeEventListener("touchmove", onMove);
      el.removeEventListener("touchend", onEnd);
      el.removeEventListener("touchcancel", onEnd);
    };
  }, [open, isFull, dismissible, onClose, dragY]);

  // Lock background scroll while open. Runs on open and unwinds on close,
  // unmount-while-open, OR a lockScroll prop flip — the cleanup always fires
  // exactly once per acquire, so stacked Sheets stay balanced.
  useEffect(() => {
    if (!open || !lockScroll) return undefined;
    acquireBodyScrollLock();
    return () => releaseBodyScrollLock();
  }, [open, lockScroll]);

  return (
    <AnimatePresence>
      {open &&
        (isFull ? (
          //--> Full-screen opaque cover (no dim backdrop — it covers everything).
          <motion.div
            key="ds-sheet-cover"
            role="dialog"
            aria-modal="true"
            aria-label={typeof title === "string" ? title : undefined}
            className={cx("ds-sheet-cover", className)}
            initial={{ y: "100%" }}
            animate={{ y: 0 }}
            exit={{ y: "100%" }}
            transition={SPRING_SMOOTH}
            {...props}
          >
            <SheetHeader title={title} dismissible={dismissible} onClose={onClose} />
            {children}
          </motion.div>
        ) : (
          //--> Auto-height bottom sheet + dim backdrop.
          <motion.div
            key="ds-sheet-backdrop"
            className="ds-sheet-backdrop"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.2 }}
            onClick={dismissible ? () => onClose?.() : undefined}
          >
            {/* Presence layer: slides the sheet up on open / down on close. Its
                transform is separate from the drag offset below, so the two never
                fight — enter/exit lives here, finger-follow lives on the inner. */}
            <motion.div
              key="ds-sheet-slide"
              className="ds-sheet-slide"
              onClick={(e) => e.stopPropagation()}
              initial={{ y: "100%" }}
              animate={{ y: 0 }}
              exit={{ y: "100%" }}
              transition={SPRING_SMOOTH}
            >
              {/* The visual card. `style={{ y: dragY }}` makes it track the finger
                  during a drag (handlers wired via sheetRef in the effect above). */}
              <motion.div
                ref={sheetRef}
                role="dialog"
                aria-modal="true"
                aria-label={typeof title === "string" ? title : undefined}
                className={cx("ds-sheet", className)}
                style={{ y: dragY }}
                {...props}
              >
                <div className="ds-sheet-top">
                  {/* Grabber = the always-available drag handle. */}
                  <div className="ds-sheet-grab" ref={grabRef}>
                    <div className="ds-sheet-grabber" aria-hidden="true" />
                  </div>
                  <SheetHeader title={title} dismissible={dismissible} onClose={onClose} />
                </div>
                {/* Scrollable content. Swiping scrolls; only a downward pull from
                    the top (or from the grabber) becomes a drag-to-dismiss. */}
                <div className="ds-sheet-body" ref={bodyRef}>{children}</div>
              </motion.div>
            </motion.div>
          </motion.div>
        ))}
    </AnimatePresence>
  );
}

export default Sheet;
